import React, { useState, useEffect } from 'react';
import moment from 'moment';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ChevronLeft, ChevronRight, Plus, User, X, Copy, CopyPlus, Ban } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = window.location.origin + '/api';

const TIME_SLOTS = ['15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30'];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const AdminCalendar = () => {
  const [slots, setSlots] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(moment().startOf('isoWeek'));
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSlotInfo, setSelectedSlotInfo] = useState(null);
  const [manualBooking, setManualBooking] = useState({ student_name: '', notes: '' });

  const authOpts = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true
    };
  };

  useEffect(() => { fetchSlots(); fetchBlockedDates(); }, []);

  const fetchSlots = async () => {
    try {
      const response = await axios.get(`${API}/admin/slots`, authOpts());
      setSlots(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        localStorage.removeItem('admin_token');
        window.location.href = '/admin';
        return;
      }
      toast.error('Error loading slots');
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const res = await axios.get(`${API}/admin/blocked-dates`, authOpts());
      setBlockedDates(res.data);
    } catch {}
  };

  const isDateBlocked = (dateStr) => blockedDates.some(b => b.date === dateStr);

  const toggleBlockDate = async (dateStr) => {
    if (isDateBlocked(dateStr)) {
      if (!window.confirm(`Unblock ${moment(dateStr).format('ddd, MMM D')}?`)) return;
      try {
        await axios.delete(`${API}/admin/blocked-dates/${dateStr}`, authOpts());
        toast.success(`Unblocked ${moment(dateStr).format('MMM D')}`);
        fetchBlockedDates();
      } catch (e) { toast.error(e.response?.data?.detail || 'Error unblocking'); }
    } else {
      const reason = window.prompt(`Block ${moment(dateStr).format('ddd, MMM D, YYYY')}?\n\nOptional reason (e.g. "ANZAC Day"):`) ?? '';
      if (reason === null) return;
      try {
        await axios.post(`${API}/admin/blocked-dates`, { date: dateStr, reason }, authOpts());
        toast.success(`Blocked ${moment(dateStr).format('MMM D')}`);
        fetchBlockedDates();
      } catch (e) { toast.error(e.response?.data?.detail || 'Error blocking'); }
    }
  };

  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(moment(selectedWeek).add(i, 'days'));
    }
    return dates;
  };

  const weekDates = getWeekDates();

  const findSlot = (date, time) => slots.find(s => s.date === date && s.start_time === time);

  const weekSlots = slots.filter(s => {
    const d = moment(s.date);
    return d.isSameOrAfter(weekDates[0], 'day') && d.isSameOrBefore(weekDates[6], 'day');
  });

  // === CLICK HANDLER ===
  const handleSlotClick = async (date, time) => {
    const slot = findSlot(date, time);

    if (slot) {
      if (window.confirm(`Remove slot at ${moment(time, 'HH:mm').format('h:mm A')} on ${moment(date).format('ddd, MMM D')}?`)) {
        try {
          await axios.delete(`${API}/admin/slots/${slot.slot_id}`, authOpts());
          toast.success('Slot removed');
          fetchSlots();
        } catch { toast.error('Error removing slot'); }
      }
    } else {
      const endTime = moment(time, 'HH:mm').add(1, 'hour').format('HH:mm');
      setSelectedSlotInfo({ date, time, endTime });
      setManualBooking({ student_name: '', notes: '' });
      setDialogOpen(true);
    }
  };

  // === CREATE SLOT (Available or Manual) ===
  const handleCreateSlot = async (type) => {
    if (!selectedSlotInfo) return;
    setCreating(true);
    try {
      const payload = {
        date: selectedSlotInfo.date,
        start_time: selectedSlotInfo.time,
        end_time: selectedSlotInfo.endTime,
        duration: 1,
        max_students: 5,
        manual_booking: type === 'manual',
        student_name: type === 'manual' ? manualBooking.student_name : null,
        notes: type === 'manual' ? manualBooking.notes : null
      };

      if (type === 'manual' && !manualBooking.student_name.trim()) {
        toast.error('Please enter student name');
        setCreating(false);
        return;
      }

      await axios.post(`${API}/admin/slots`, payload, authOpts());
      toast.success(type === 'manual' ? `Booked ${manualBooking.student_name}!` : 'Slot opened!');
      setDialogOpen(false);
      fetchSlots();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreating(false);
    }
  };

  // === COPY TO NEXT WEEK ===
  const handleCopyWeekToNext = async () => {
    if (weekSlots.length === 0) {
      toast.error('No slots in current week to copy');
      return;
    }
    if (!window.confirm(`Copy ${weekSlots.length} slot(s) to next week?`)) return;
    setCreating(true);
    let created = 0;
    for (const slot of weekSlots) {
      const nextDate = moment(slot.date).add(7, 'days').format('YYYY-MM-DD');
      const existing = slots.find(s => s.date === nextDate && s.start_time === slot.start_time);
      if (existing) continue;
      try {
        await axios.post(`${API}/admin/slots`, {
          date: nextDate,
          start_time: slot.start_time,
          end_time: slot.end_time,
          duration: slot.duration || 1,
          max_students: slot.max_students || 5,
          manual_booking: slot.manual_booking || false,
          student_name: slot.student_name || null,
          notes: slot.notes || null
        }, authOpts());
        created++;
      } catch {}
    }
    toast.success(created > 0 ? `Copied ${created} slot(s) to next week!` : 'All slots already exist in next week');
    fetchSlots();
    if (created > 0) setSelectedWeek(moment(selectedWeek).add(1, 'week'));
    setCreating(false);
  };

  // === COPY TO 10 WEEKS ===
  const handleCopyTo10Weeks = async () => {
    if (weekSlots.length === 0) {
      toast.error('No slots in current week to copy');
      return;
    }
    if (!window.confirm(`Copy ${weekSlots.length} slot(s) to the next 10 weeks?\n\nThis will create slots for weeks 1-10 ahead.`)) return;
    setCreating(true);
    let totalCreated = 0;
    for (const slot of weekSlots) {
      try {
        const res = await axios.post(`${API}/admin/slots/copy-weeks`, {
          slot_id: slot.slot_id,
          num_weeks: 10
        }, authOpts());
        const match = res.data.message?.match(/Created (\d+)/);
        if (match) totalCreated += parseInt(match[1]);
      } catch {}
    }
    toast.success(totalCreated > 0 ? `Created ${totalCreated} slots across 10 weeks!` : 'All slots already exist');
    fetchSlots();
    setCreating(false);
  };

  // === DELETE ALL ===
  const handleDeleteAll = async () => {
    if (window.prompt(`Delete ${weekSlots.length} slot(s) from this week?\n\nType "DELETE" to confirm:`) !== 'DELETE') return;
    setCreating(true);
    let count = 0;
    for (const slot of weekSlots) {
      try { await axios.delete(`${API}/admin/slots/${slot.slot_id}`, authOpts()); count++; } catch {}
    }
    toast.success(`Deleted ${count} slot(s)`);
    fetchSlots();
    setCreating(false);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>Weekly Availability</h2>

      {/* Week Navigation + Actions */}
      <Card className="card-feature p-4">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <Button variant="outline" size="sm" onClick={() => setSelectedWeek(moment(selectedWeek).subtract(1, 'week'))}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Prev Week
          </Button>

          <div className="text-center flex-1">
            <h3 className="font-semibold text-lg">
              {weekDates[0].format('MMM D')} – {weekDates[6].format('MMM D, YYYY')}
            </h3>
            <p className="text-xs text-muted-foreground">{weekSlots.length} slot(s) this week</p>
          </div>

          <Button variant="outline" size="sm" onClick={() => setSelectedWeek(moment(selectedWeek).add(1, 'week'))}>
            Next Week <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            data-testid="copy-next-week-btn"
            onClick={handleCopyWeekToNext}
            disabled={creating || weekSlots.length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy to Next Week
          </Button>

          <Button
            data-testid="copy-10-weeks-btn"
            onClick={handleCopyTo10Weeks}
            disabled={creating || weekSlots.length === 0}
            className="bg-green-600 hover:bg-green-700 text-white"
            size="sm"
          >
            <CopyPlus className="w-4 h-4 mr-2" />
            Copy to 10 Weeks
          </Button>

          {weekSlots.length > 0 && (
            <Button
              data-testid="delete-week-btn"
              variant="destructive"
              size="sm"
              onClick={handleDeleteAll}
              disabled={creating}
            >
              <X className="w-4 h-4 mr-2" />
              Delete This Week
            </Button>
          )}
        </div>

        {/* Calendar Grid */}
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Header */}
            <div className="grid grid-cols-8 gap-2 mb-2">
              <div className="font-semibold text-sm text-muted-foreground text-center">Time</div>
              {weekDates.map((date, idx) => {
                const isToday = date.isSame(moment(), 'day');
                const dateStr = date.format('YYYY-MM-DD');
                const blocked = isDateBlocked(dateStr);
                return (
                  <div key={idx} className="text-center relative">
                    <div className={`p-3 rounded-lg font-semibold text-sm ${blocked ? 'bg-red-100 text-red-700 line-through' : isToday ? 'bg-primary text-white' : 'bg-muted'}`}>
                      <div>{DAYS[idx]}</div>
                      <div className="text-xs font-normal mt-1">{date.format('MMM D')}</div>
                    </div>
                    <button
                      data-testid={`block-date-${dateStr}`}
                      onClick={() => toggleBlockDate(dateStr)}
                      className={`mt-1 text-[10px] px-2 py-0.5 rounded-full transition-colors ${blocked ? 'bg-red-200 text-red-700 hover:bg-red-300' : 'bg-muted text-muted-foreground hover:bg-red-100 hover:text-red-600'}`}
                    >
                      {blocked ? 'Unblock' : 'Block'}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Time Rows */}
            {TIME_SLOTS.map((time) => (
              <div key={time} className="grid grid-cols-8 gap-2 mb-2">
                <div className="flex items-center justify-center font-medium text-sm text-muted-foreground">
                  {moment(time, 'HH:mm').format('h:mm A')}
                </div>

                {weekDates.map((date, idx) => {
                  const dateStr = date.format('YYYY-MM-DD');
                  const blocked = isDateBlocked(dateStr);
                  const slot = findSlot(dateStr, time);

                  if (blocked) {
                    return (
                      <div key={idx} className="w-full h-14 rounded-lg bg-red-50 border-2 border-red-200 flex items-center justify-center text-xs text-red-400">
                        <Ban className="w-3 h-3 mr-1" /> Blocked
                      </div>
                    );
                  }

                  if (!slot) {
                    return (
                      <button
                        key={idx}
                        onClick={() => handleSlotClick(dateStr, time)}
                        disabled={creating}
                        className="w-full h-14 rounded-lg border-2 border-dashed border-muted hover:border-primary hover:bg-primary/5 transition-all text-xs text-muted-foreground"
                      >
                        + Add
                      </button>
                    );
                  }

                  const isManual = slot.manual_booking;
                  const isFull = !slot.available;

                  return (
                    <button
                      key={idx}
                      onClick={() => handleSlotClick(dateStr, time)}
                      disabled={creating}
                      className={`w-full h-14 rounded-lg border-2 transition-all text-xs font-medium flex flex-col items-center justify-center gap-0.5 ${
                        isManual
                          ? 'bg-amber-50 border-amber-400 text-amber-700 hover:border-amber-500'
                          : isFull
                          ? 'bg-red-50 border-red-300 text-red-600'
                          : 'bg-green-50 border-green-400 text-green-700 hover:border-green-500'
                      }`}
                    >
                      {isManual ? (
                        <>
                          <User className="w-3 h-3" />
                          <span className="truncate max-w-[90%]">{slot.student_name}</span>
                        </>
                      ) : isFull ? (
                        <span>Full</span>
                      ) : (
                        <>
                          <span>Open</span>
                          <span className="text-[10px] opacity-70">{slot.booked_students}/{slot.max_students} booked</span>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Legend */}
      <Card className="card-feature p-4 bg-muted/30">
        <div className="flex flex-wrap gap-6 justify-center text-sm">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-50 border-2 border-green-400 rounded flex items-center justify-center text-[10px] text-green-700 font-medium">Open</div>
            <span>Available for booking</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-50 border-2 border-amber-400 rounded flex items-center justify-center"><User className="w-3 h-3 text-amber-700" /></div>
            <span>Manual booking</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-50 border-2 border-red-300 rounded flex items-center justify-center text-[10px] text-red-600 font-medium">Full</div>
            <span>Fully booked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 border-2 border-dashed border-muted rounded"></div>
            <span>No slot</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-50 border-2 border-red-200 rounded flex items-center justify-center"><Ban className="w-3 h-3 text-red-400" /></div>
            <span>Blocked date</span>
          </div>
        </div>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="card-feature p-4 text-center">
          <p className="text-2xl font-bold text-primary">{slots.length}</p>
          <p className="text-sm text-muted-foreground">Total Slots</p>
        </Card>
        <Card className="card-feature p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{slots.filter(s => s.available && !s.manual_booking).length}</p>
          <p className="text-sm text-muted-foreground">Open for Booking</p>
        </Card>
        <Card className="card-feature p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{slots.filter(s => s.manual_booking).length}</p>
          <p className="text-sm text-muted-foreground">Manual Bookings</p>
        </Card>
        <Card className="card-feature p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{blockedDates.length}</p>
          <p className="text-sm text-muted-foreground">Blocked Dates</p>
        </Card>
      </div>

      {/* Create Slot Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Time Slot</DialogTitle>
            <DialogDescription>Open this slot for parents to book, or book a student directly.</DialogDescription>
          </DialogHeader>

          {selectedSlotInfo && (
            <div className="space-y-4 mt-2">
              <div className="bg-muted/50 p-3 rounded-lg text-sm">
                <strong>{moment(selectedSlotInfo.date).format('dddd, MMM D, YYYY')}</strong>
                <br />
                {moment(selectedSlotInfo.time, 'HH:mm').format('h:mm A')} – {moment(selectedSlotInfo.endTime, 'HH:mm').format('h:mm A')} (1 hour)
              </div>

              {/* Option 1: Open for Booking */}
              <button
                data-testid="create-open-slot-btn"
                onClick={() => handleCreateSlot('available')}
                disabled={creating}
                className="w-full p-4 border-2 border-green-500 rounded-lg hover:bg-green-50 transition-all text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Plus className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="font-semibold">Open for Online Booking</div>
                    <div className="text-sm text-muted-foreground">Parents choose session type & pay online</div>
                  </div>
                </div>
              </button>

              {/* Option 2: Manual Booking */}
              <div className="border-2 border-amber-400 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <User className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <div className="font-semibold">Book a Student Directly</div>
                    <div className="text-sm text-muted-foreground">Phone / in-person booking</div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Student Name *</Label>
                    <Input
                      data-testid="manual-student-name"
                      placeholder="Enter student name"
                      className="mt-1"
                      value={manualBooking.student_name}
                      onChange={(e) => setManualBooking({ ...manualBooking, student_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Notes (optional)</Label>
                    <Textarea
                      placeholder="Subject, parent contact, etc."
                      className="mt-1"
                      rows={2}
                      value={manualBooking.notes}
                      onChange={(e) => setManualBooking({ ...manualBooking, notes: e.target.value })}
                    />
                  </div>
                  <Button
                    data-testid="create-manual-slot-btn"
                    onClick={() => handleCreateSlot('manual')}
                    disabled={creating || !manualBooking.student_name.trim()}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    Book Student
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCalendar;
